import { z } from "zod";

// A single logged food / nutrition entry (slice 3, metabolic correlation).
// Special-category-adjacent health data: per-user partition, never logged,
// wiped on account delete. Imported from Health Connect (Nutrition, one record
// per food) or entered by hand.
//
// Stored as its own DDB row (pk=USER#<sub>, sk=NUTRITION#<13-digit-ms>#<id>),
// a time-series entity so a date-window read is an O(window) range query.
//
// We keep the macros that matter for the glucose↔carbs↔training correlation
// (carbohydrate first); calories + protein + fat are optional context.

const Grams = z.number().min(0).max(2000);
const Kcal = z.number().min(0).max(10000);

export const NutritionEntry = z.object({
  id: z.uuid(),
  userSub: z.string().min(1).max(128),
  takenAt: z.iso.datetime(),
  // Food name as HC reports it (e.g. "Apple", "Hard-boiled egg"). Optional —
  // a bare macro entry is still valid.
  name: z.string().min(1).max(160).optional(),
  carbsG: Grams.optional(),
  proteinG: Grams.optional(),
  fatG: Grams.optional(),
  kcal: Kcal.optional(),
  // HC meal grouping label (e.g. "breakfast", "lunch", "snack") when present.
  mealType: z.string().min(1).max(32).optional(),
  // Provenance. Absent ⇒ JL-entered. "hc:<pkg>" for a Health Connect import.
  // externalId = HC record id (dedup/idempotency). Set on import, not patched.
  source: z.string().min(1).max(96).optional(),
  externalId: z.string().min(1).max(256).optional(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type NutritionEntry = z.infer<typeof NutritionEntry>;

// Client → server payload. Server fills userSub/createdAt/updatedAt.
export const NewNutritionEntry = NutritionEntry.omit({
  id: true,
  userSub: true,
  createdAt: true,
  updatedAt: true,
}).extend({ id: z.uuid().optional() });
export type NewNutritionEntry = z.infer<typeof NewNutritionEntry>;
